import React, { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { deleteNetwork, type NetworkRecord } from "@services/networkManagement";

export interface NetworkDeleteDialogProps {
  open: boolean;
  network: NetworkRecord | null;
  onClose: () => void;
  onDeleted: (result: {
    network_id: number;
    network_name: string;
    unlinked_count: number;
    deleted_orphan_count: number;
  }) => void;
}

export default function NetworkDeleteDialog({
  open,
  network,
  onClose,
  onDeleted,
}: NetworkDeleteDialogProps) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (deleting) return;
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!network?.id || deleting) return;
    setDeleting(true);
    setError(null);
    try {
      const result = await deleteNetwork(network.id);
      onDeleted(result);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to delete network");
    } finally {
      setDeleting(false);
    }
  };

  const linkedCount = network?.linked_ccn_count ?? network?.child_facility_count ?? 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: "#b42318", fontWeight: 600 }}>
        Delete network permanently?
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Typography sx={{ mb: 1.5 }}>
          This will permanently delete{" "}
          <strong>{network?.name ?? "this network"}</strong>
          {network?.id != null ? (
            <>
              {" "}
              (network ID {network.id})
            </>
          ) : null}{" "}
          and remove all user associations. This action cannot be undone.
        </Typography>
        {linkedCount > 0 ? (
          <Typography sx={{ color: "#4a6670", fontSize: 14 }}>
            {linkedCount} linked {linkedCount === 1 ? "facility" : "facilities"} will be unlinked.
            Import-created facilities with no user setup will be removed; facilities that other
            users have registered will only be unlinked from this network.
          </Typography>
        ) : (
          <Typography sx={{ color: "#4a6670", fontSize: 14 }}>
            This network has no linked facilities.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={deleting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          disabled={deleting || !network?.id}
          color="error"
          variant="contained"
          sx={{ textTransform: "none" }}
        >
          {deleting ? "Deleting…" : "Delete network"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
